import React, { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import {
  AppBar,
  Grid,
  IconButton,
  makeStyles,
  Theme,
  Toolbar,
  Tooltip,
  Typography,
  useTheme,
  Brightness2,
  KeyboardDoubleArrowLeft,
  KeyboardDoubleArrowRight,
  LightModeOutlined,
  MenuIcon,
} from '@quillbot/design-system';
import Link from 'next/link';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'src/_common/models/redux/store';
import ShowInMobile from 'src/_common/quillElements/fragment/ShowInMobile';
import StyledPremiumButton from '@Common/quillElements/button/PremiumButton';
import { APP_SETTINGS } from 'config/constants';
import QuillLocalStorage from 'core/utils/localStorageClient';
import { setRegionalLanguage, setSidebarPinnned } from '@Common/actions/commonActions';
import QuillLogo from './HeaderLogo';
/* extra header code */
import HeaderExt from './HeaderExt';
import AccountMenu from './AccountMenu';
import LanguageMenu from './LanguageMenu';
import UseCoWriterButton from './UseCoWriteButton';
import Img from '@Common/components/img';
import { switchLanguage } from '@Common/handlers/services';
import { ABExperiments, ABTestWithFallbackCtrl } from '@Common/hoc';

const PAGE_TITLES: { [key: string]: string } = {
  '/': 'Paraphraser',
  '/grammar-check': 'Grammar Checker',
  '/plagiarism-checker': 'Plagiarism Checker',
  '/co-writer': 'Co-Writer',
  '/summarize': 'Summarizer',
  '/citation-generator': 'Citation Generator',
  '/premium': 'QuillBot Premium',
  '/student-resources': 'Student Resources',
  '/guides/paraphrasing': 'Paraphrasing Guide',
  '/contact': 'Contact Us',
  '/expert-review': 'Scribbr’s Expert Review',
  // '/ambassador': 'Ambassdor Page',
};

const useStyles = makeStyles<Theme, StyleProps>((theme) => ({
  offset: theme.mixins.toolbar,
  headerToolbar: {
    minHeight: '52px',
    [theme.breakpoints.down('lg')]: {
      minHeight: '45px',
    },
  },
  productName: {
    justifyContent: 'center',
  },
  headerActions: {
    justifyContent: 'flex-end',
    paddingRight: 15,
  },
  quillHeader: {
    borderBottom: '1px solid',
    color: theme.palette.divider,
  },
  premiumButtonDisplay: {
    display: 'none',
    [theme.breakpoints.up('md')]: {
      display: 'flex',
    },
  },
  extensionSidebar: {
    display: 'none !important',
    [theme.breakpoints.down('lg')]: {
      display: 'flex',
    },
  },
  loginButtons: {
    textTransform: 'capitalize',
    fontWeight: 600,
    padding: '5px 8px',
    [theme.breakpoints.down('sm')]: {
      display: 'none',
    },
  },
  premiumTitle: {
    fontSize: '0.75em',
    letterSpacing: '1px',
    color: theme.palette.secondary.main,
    fontWeight: 600,
    marginTop: '10px',
    display: 'flex',
    alignSelf: 'flex-end',
    marginLeft: '5px',
    fontFamily: 'Open Sans',
    position: 'relative',
    top: '-5px',
  },
  extensionBtn: {
    [theme.breakpoints.down(1080)]: {
      display: 'none',
    },
  },
  premiuimBtnFullText: {
    marginRight: '12px',
    [theme.breakpoints.down(370)]: {
      display: 'none',
    },
    [theme.breakpoints.down('sm')]: {
      marginRight: '0px',
    },
  },
  premiuimBtnHalfText: {
    display: 'none',
    [theme.breakpoints.down(450)]: {
      display: 'inline-flex',
      width: 'inherit !important',
      padding: '6px 12px !important',
      minWidth: '85px !important',
      fontSize: 13,
      fontWeight: '600 !important',
      lineHeight: 1.4,
    },
  },

  /* new css code */
  quillLogo: {
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'center',
    marginRight: '18px',
    [theme.breakpoints.up(1600)]: {
      marginLeft: 6,
    },

    [theme.breakpoints.down(900)]: {
      minHeight: '48px',
      marginRight: '22px',
    },

    [theme.breakpoints.down(600)]: {
      marginLeft: '10px',
      marginRight: '10px',
    },
  },

  navBtn: {
    background: theme.palette.primary.main,
  },

  mainMenuBtn: {
    padding: '4px',
    background: theme.palette.background.default,
    marginRight: 0,
    marginLeft: '10px',
    width: 32,
    height: 32,
  },
  languageBtn: {
    [theme.breakpoints.down('sm')]: {
      display: 'none',
    },
  },
  toolName: {
    color: theme.palette.text.primary,
    fontWeight: 700,
    fontSize: '16px',
    lineHeight: '22px',
  },
  userIcon: {
    width: 24,
    height: 24,
    fontWeight: 700,
    fontSize: '20px',
    lineHeight: '24px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: theme.specialColors.ICON_DEFAULT_COLOR,
    textTransform: 'uppercase',
  },
  hamburger: {
    margin: '8px 12px 8px 8px',
    color: theme.specialColors.ICON_DEFAULT_COLOR,
    [theme.breakpoints.down('sm')]: {
      margin: 0,
      paddingRight: 0,
    },
  },
  desktopHamburger: {
    display: 'inline-flex',
    minHeight: 24,
    minWidth: 24,
    position: 'relative',
    [theme.breakpoints.down('sm')]: {
      display: 'none',
    },
  },
  mobileHamburger: {
    display: 'none',
    [theme.breakpoints.down('sm')]: {
      display: 'inline-flex',
    },
  },
  hamburgerIcon: {
    position: 'absolute',
    transition: 'opacity 0.3s ease',
  },
}));

interface StyleProps {
  isBannerShown: boolean;
  isMobile: boolean;
}
interface HeaderProps {
  handleThemeChange: Function;
  LANGUAGES: any;
  isBannerShown: boolean;
}

function Header(props: HeaderProps) {
  const router = useRouter();
  const userState = useSelector((rootState: RootState) => rootState.user);
  const platform = useSelector((rootState: RootState) => rootState.common.platform);
  const sidebarPinned = useSelector((rootState: RootState) => rootState.common.sidebarPinned);
  const isSubscriptionPaused = useSelector((state: RootState) => state.user?.isSubPaused);

  const isKeyboardOpen = useSelector((rootState: RootState) => rootState.common.isKeyboardOpen);
  const isMobile = useSelector((rootState: RootState) => rootState.common.platform.isMobile);
  const regionalLanguage = useSelector((rootState: RootState) => rootState?.common.regionalLanguage);
  const isCampaign = !!useSelector((rootState: RootState) => rootState?.common.campaign);

  const [pageTitle, setPageTitle] = useState<string>();
  const [hamburgerHovered, setHamburgerHovered] = useState<boolean>(false);
  const [languageMenuOpen, setLanguageMenuOpen] = useState<boolean>(false);
  const theme = useTheme();
  const dispatch = useDispatch();
  const classes = useStyles({ isBannerShown: props.isBannerShown, isMobile });

  useEffect(() => {
    setPageTitle(PAGE_TITLES[router.pathname]);
  }, [router.pathname]);

  useEffect(() => {
    if (Object.keys(props.LANGUAGES).includes(APP_SETTINGS.REGION)) dispatch(setRegionalLanguage(APP_SETTINGS.REGION));
    else {
      dispatch(setRegionalLanguage('us'));
      QuillLocalStorage.getInstance().set('REGION', 'us');
      APP_SETTINGS.REGION = 'us';
    }
  }, []);

  const shouldRenderPremiumButton =
    !router.pathname.includes('/premium') &&
    !router.pathname.includes('/upgrade') &&
    !router.pathname.includes('/workshop') &&
    !router.pathname.includes('/workshopSuccess') &&
    !(router.pathname.includes('/settings') && isSubscriptionPaused) &&
    !platform.native &&
    !userState?.premium;

  const renderQuillBot = () => (
    <Link href={isCampaign ? 'https://quillbot.com' : '/'}>
      <a className={classes.quillLogo}>
        <div style={{ display: 'flex' }}>
          <QuillLogo />
          {userState?.premium ? <div className={classes.premiumTitle}>PREMIUM</div> : null}
        </div>
      </a>
    </Link>
  );

  const isExtension = platform.isExtension === true;

  const allowedRoutes = ['/', '/summarize', '/grammar-check'];

  if (!isExtension) {
    allowedRoutes.push('/plagiarism-checker');
  }

  const isProduct = allowedRoutes.includes(router.pathname);

  const isCoWriter = router.pathname.includes('/co-writer');

  if (isKeyboardOpen) {
    return null;
  }

  const handleLanguageChange = (language: string) => {
    switchLanguage(language);
    setLanguageMenuOpen(false);
  };

  const renderThemeButton = () =>
    !isMobile &&
    ![
      '/co-writer',
      '/citation-generator',
      '/extension-success',
      'exit-survey',
      '/guides/paraphrasing',
      '/plagiarism-checker',
      '/expert-review',
      '/ambassador',
      '/cookie-policy',
    ].includes(router.pathname) && (
      <Tooltip title={theme.palette.mode === 'light' ? 'Switch to Dark Mode' : 'Switch to Light Mode'}>
        {theme.palette.mode === 'light' ? (
          <IconButton edge="end" className={classes.mainMenuBtn} onClick={() => props.handleThemeChange()} size="large">
            <Brightness2 style={{ transform: 'rotate(150deg)', fontSize: '18px' }} />
          </IconButton>
        ) : (
          <IconButton edge="end" className={classes.mainMenuBtn} onClick={() => props.handleThemeChange()} size="large">
            <LightModeOutlined />
          </IconButton>
        )}
      </Tooltip>
    );

  return (
    <>
      {!isExtension && (
        <AppBar position="relative" elevation={1} color="inherit" className={classes.quillHeader}>
          <Grid container justifyContent="space-between">
            <Grid item flex={1} justifyContent="flex-start">
              <Toolbar disableGutters className={classes.headerToolbar} style={{ paddingRight: 0, paddingLeft: 5 }}>
                <IconButton
                  onClick={() => {
                    if (!isCoWriter) {
                      dispatch(setSidebarPinnned(!sidebarPinned, 'user'));
                      QuillLocalStorage.getInstance().set('sidebarPinned', String(!sidebarPinned));
                    }
                  }}
                  className={`${classes.hamburger} ${classes.desktopHamburger}`}
                  onTouchStart={() => !isCoWriter && setHamburgerHovered(false)}
                  onMouseEnter={() => !isCoWriter && setHamburgerHovered(true)}
                  onMouseLeave={() => !isCoWriter && setHamburgerHovered(false)}
                  onTouchEnd={() => !isCoWriter && setHamburgerHovered(false)}>
                  <MenuIcon className={classes.hamburgerIcon} style={{ opacity: hamburgerHovered ? 0 : 1 }} />
                  <KeyboardDoubleArrowLeft
                    className={classes.hamburgerIcon}
                    style={{
                      opacity: hamburgerHovered && sidebarPinned ? 1 : 0,
                    }}
                  />
                  <KeyboardDoubleArrowRight
                    className={classes.hamburgerIcon}
                    style={{
                      opacity: hamburgerHovered && !sidebarPinned ? 1 : 0,
                    }}
                  />
                </IconButton>
                {renderQuillBot()}
              </Toolbar>
            </Grid>
            {!isMobile && !isCampaign && (
              <Grid item flex={1}>
                <Toolbar disableGutters className={`${classes.headerToolbar} ${classes.productName}`}>
                  <Typography className={classes.toolName}>{pageTitle}</Typography>
                </Toolbar>
              </Grid>
            )}
            <Grid item flex={1} justifyContent="flex-end">
              <Toolbar
                disableGutters
                style={{ paddingLeft: 0 }}
                className={`${classes.headerToolbar} ${classes.headerActions}`}>
                {shouldRenderPremiumButton && (
                  <Grid item>
                    {isMobile ? (
                      <StyledPremiumButton
                        className={classes.premiuimBtnFullText}
                        style={{
                          paddingLeft: '13.5px',
                          paddingRight: '13.5px',
                        }}
                        referrer="header"
                        text={isMobile ? 'Premium' : 'Upgrade to Premium'}
                        native={platform.native}
                      />
                    ) : (
                      <ABTestWithFallbackCtrl
                        experiment={ABExperiments.premiumCTA_August2022}
                        variants={{
                          A: (
                            <StyledPremiumButton
                              className={classes.premiuimBtnFullText}
                              style={{
                                paddingLeft: '13.5px',
                                paddingRight: '13.5px',
                              }}
                              referrer="header"
                              text="Explore Premium"
                              native={platform.native}
                            />
                          ),
                          B: (
                            <StyledPremiumButton
                              className={classes.premiuimBtnFullText}
                              style={{
                                paddingLeft: '13.5px',
                                paddingRight: '13.5px',
                              }}
                              referrer="header"
                              text="Discover Premium Benefits"
                              native={platform.native}
                            />
                          ),
                          C: (
                            <StyledPremiumButton
                              className={classes.premiuimBtnFullText}
                              style={{
                                paddingLeft: '13.5px',
                                paddingRight: '13.5px',
                              }}
                              referrer="header"
                              text="Save Time Writing"
                              native={platform.native}
                            />
                          ),
                          D: (
                            <StyledPremiumButton
                              className={classes.premiuimBtnFullText}
                              style={{
                                paddingLeft: '13.5px',
                                paddingRight: '13.5px',
                              }}
                              referrer="header"
                              text="Upgrade to Premium"
                              native={platform.native}
                            />
                          ),
                        }}
                        fallback={
                          <StyledPremiumButton
                            className={classes.premiuimBtnFullText}
                            style={{
                              paddingLeft: '13.5px',
                              paddingRight: '13.5px',
                            }}
                            referrer="header"
                            text="Upgrade to Premium"
                            native={platform.native}
                          />
                        }
                      />
                    )}
                  </Grid>
                )}

                {router.pathname.includes('/citation-generator') && userState.premium && (
                  <Grid item className={classes.extensionBtn}>
                    <UseCoWriterButton />
                  </Grid>
                )}

                <IconButton
                  id="language-menu-button"
                  edge="end"
                  className={`${classes.mainMenuBtn} ${classes.languageBtn}`}
                  aria-haspopup="true"
                  onMouseEnter={() => setLanguageMenuOpen(true)}
                  onMouseLeave={() => setLanguageMenuOpen(false)}
                  size="large"
                  style={{ padding: 0 }}>
                  <Img
                    src={props.LANGUAGES[regionalLanguage].imagePath}
                    alt="language menu"
                    style={{
                      width: '30px',
                      height: '30px',
                      borderRadius: '50%',
                    }}
                  />
                </IconButton>
                {renderThemeButton()}
                <AccountMenu />
                <IconButton
                  onClick={() => {
                    dispatch(setSidebarPinnned(!sidebarPinned, 'user'));
                  }}
                  className={`${classes.hamburger} ${classes.mobileHamburger}`}>
                  <MenuIcon />
                </IconButton>
              </Toolbar>
            </Grid>
          </Grid>
        </AppBar>
      )}
      <LanguageMenu
        open={languageMenuOpen}
        setLanguageMenuOpen={setLanguageMenuOpen}
        handleLanguageChange={handleLanguageChange}
        LANGUAGES={props.LANGUAGES}
      />
      <ShowInMobile>{isProduct && <HeaderExt isExtension={isExtension} />}</ShowInMobile>
    </>
  );
}

export default Header;
